"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { InspectResult } from "@/lib/music/analyzer-types";
import {
  resolveNeedsHelpReason,
  trackAuditionGlobalPlayed,
  trackAuditionSoloPlayed,
  trackBypassed,
  trackNeedsHelpShown,
  trackSelectionChanged,
  trackOtherTracksExpanded,
  trackValidated,
  type NeedsHelpReason,
} from "@/lib/telemetry/midi-quality-events";

import { AuditionPlayerButton } from "./audition-player-button";
import { isProbableTrack } from "./track-classification";
import { TrackSelector } from "./track-selector";
import { useAuditionPlayer } from "./use-audition-player";

/** V2 gradient used for the H1 heading (matches `LaunchingOverlay` in `UploadModalV2`). */
const HEADING_GRADIENT =
  "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.72) 100%)";

interface UploadHelpScreenProps {
  inspect: InspectResult;
  /*
    Identifiant opaque de l'import, tiré par `UploadModal` à la réception du
    fichier. Il ne sert qu'à joindre entre eux les huit événements du flux de
    qualité, et l'écran ne l'affiche nulle part.

    C'était le nom du fichier, annoncé « display-only » alors qu'aucun JSX ne le
    rendait : le commentaire mentait, et le nom que la personne a donné à son
    fichier partait dans sept charges utiles. Il est tiré en amont, et pas ici,
    pour que l'abandon émis par la modale porte le même identifiant que les
    sept autres.
  */
  fileId: string;
  onContinue: (selectedTracks: number[]) => void;
  onChooseAnother: () => void;
  onBypass: () => void;
}

/** Resolve wording variant (H1 + body) from the active flags. */
function resolveWording(reason: NeedsHelpReason): {
  title: string;
  subtitle: string;
  body: string;
} {
  if (reason === "no_piano") {
    return {
      title: "On n'a pas trouvé de piano",
      subtitle: "",
      body:
        "Aucune piste n'a été identifiée comme du piano. Écoute le fichier et choisis manuellement ce que tu veux jouer.",
    };
  }

  if (reason === "drums_dominant") {
    return {
      title: "On entend surtout de la batterie",
      subtitle: "",
      body:
        "Ton fichier contient beaucoup de percussions. Si du piano s'y cache, écoute les pistes et coche celles à garder.",
    };
  }

  if (reason === "arrangement") {
    return {
      title: "Ce morceau a plusieurs instruments",
      subtitle: "",
      body:
        "C'est un arrangement complet (basse, batterie, guitares…), pas une partition piano. Écoute les pistes et coche celles à jouer au piano — en général la mélodie et la basse.",
    };
  }

  return {
    title: "On a besoin d'aide",
    subtitle: "pour reconnaître ton piano",
    body:
      "On a regardé ton fichier mais on n'a pas su identifier la piste piano avec certitude. Écoute-le et coche les pistes à garder.",
  };
}

/** Computes the initial selection: every "probable" track pre-checked. */
function buildInitialSelection(inspect: InspectResult): Set<number> {
  const out = new Set<number>();

  if (!inspect.audition) {
    return out;
  }

  inspect.audition.tracks.forEach((t, idx) => {
    const inspectTrack = inspect.tracks.find((it) => it.index === t.index);

    if (isProbableTrack(t, inspectTrack)) {
      out.add(idx);
    }
  });

  return out;
}

/** Aggregate 24-bucket per-track densities to a single 60-bucket global view. */
function aggregateGlobalBuckets(inspect: InspectResult): number[] {
  if (!inspect.audition || inspect.audition.tracks.length === 0) {
    return new Array<number>(60).fill(0);
  }

  // Pull from each per-track bar (24) then resample to 60 with weight conservation.
  const SOURCE_BUCKETS = 24;
  const TARGET_BUCKETS = 60;
  const merged = new Array<number>(SOURCE_BUCKETS).fill(0);

  for (const track of inspect.audition.tracks) {
    for (let i = 0; i < Math.min(track.densityBuckets.length, SOURCE_BUCKETS); i++) {
      merged[i] += track.densityBuckets[i];
    }
  }

  const ratio = SOURCE_BUCKETS / TARGET_BUCKETS;
  const out = new Array<number>(TARGET_BUCKETS).fill(0);

  for (let i = 0; i < TARGET_BUCKETS; i++) {
    const start = i * ratio;
    const end = (i + 1) * ratio;
    const startIdx = Math.floor(start);
    const endIdx = Math.min(merged.length - 1, Math.floor(end - 1e-9));

    let acc = 0;
    for (let k = startIdx; k <= endIdx; k++) {
      const overlapStart = Math.max(start, k);
      const overlapEnd = Math.min(end, k + 1);
      acc += merged[k] * Math.max(0, overlapEnd - overlapStart);
    }

    out[i] = acc;
  }

  return out;
}

export function UploadHelpScreen({
  inspect,
  fileId,
  onContinue,
  onChooseAnother,
  onBypass,
}: UploadHelpScreenProps) {
  const audition = inspect.audition ?? null;
  const reason = useMemo<NeedsHelpReason>(
    () => resolveNeedsHelpReason(inspect.quality.kind === "needs_help" ? inspect.quality.flags : []),
    [inspect.quality],
  );

  const wording = useMemo(() => resolveWording(reason), [reason]);

  const [selected, setSelected] = useState<Set<number>>(() => buildInitialSelection(inspect));
  const { state, player } = useAuditionPlayer(audition);

  // Keep the live preview mix in sync with the checked set: toggling a track
  // adds/removes it from what's playing (restarting from the playhead).
  const selectedTracks = useMemo(() => [...selected], [selected]);

  useEffect(() => {
    player?.updateSelection(selectedTracks);
  }, [player, selectedTracks]);

  const globalDensity = useMemo(() => aggregateGlobalBuckets(inspect), [inspect]);

  // Telemetry counters — kept in refs so handlers don't churn on every render.
  const shownAtRef = useRef(performance.now());
  const auditionGlobalUsedRef = useRef(false);
  const auditionSoloCountRef = useRef(0);
  const selectionChangedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flags = inspect.quality.kind === "needs_help" ? inspect.quality.flags : [];

  const trackCount = audition?.tracks.length ?? 0;
  const probableCount = useMemo(
    () => Array.from(selected.values()).filter((idx) => isProbable(inspect, idx)).length,
    [inspect, selected],
  );

  // Fire `needs_help_shown` once when the screen mounts.
  useEffect(() => {
    if (!audition) {
      return;
    }

    const initialProbable = buildInitialSelection(inspect).size;
    trackNeedsHelpShown({
      fileId,
      reason,
      flags,
      trackCount,
      probableCount: initialProbable,
      durationSec: audition.durationSec,
      totalNotes: inspect.metadata.totalNotes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(
    (trackIdx: number) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const becomingChecked = !next.has(trackIdx);

        if (becomingChecked) {
          next.add(trackIdx);
        } else {
          next.delete(trackIdx);
        }

        // Debounced selection_changed event — spec §7 mentions 300ms.
        if (selectionChangedTimerRef.current) {
          clearTimeout(selectionChangedTimerRef.current);
        }

        const probable = isProbable(inspect, trackIdx);
        selectionChangedTimerRef.current = setTimeout(() => {
          trackSelectionChanged({
            fileId,
            trackIndex: trackIdx,
            checked: becomingChecked,
            isProbable: probable,
            currentSelectedCount: next.size,
          });
        }, 300);

        return next;
      });
    },
    [inspect, fileId],
  );

  const handleGlobalPlayed = useCallback(() => {
    if (auditionGlobalUsedRef.current) {
      return;
    }

    auditionGlobalUsedRef.current = true;
    trackAuditionGlobalPlayed({
      fileId,
      msSinceShown: performance.now() - shownAtRef.current,
      samplesLoadMs: null,
    });
  }, [fileId]);

  const handleSoloPlayed = useCallback(
    (trackIdx: number) => {
      auditionSoloCountRef.current += 1;
      const track = audition?.tracks[trackIdx];

      if (!track) {
        return;
      }

      trackAuditionSoloPlayed({
        fileId,
        trackIndex: trackIdx,
        programGM: track.gmProgram,
        isProbable: isProbable(inspect, trackIdx),
        msSinceShown: performance.now() - shownAtRef.current,
      });
    },
    [audition, fileId, inspect],
  );

  const handleOtherTracksExpanded = useCallback(
    (otherCount: number) => {
      trackOtherTracksExpanded({
        fileId,
        otherCount,
        msSinceShown: performance.now() - shownAtRef.current,
      });
    },
    [fileId],
  );

  const handleValidate = useCallback(() => {
    if (selected.size === 0) {
      return;
    }

    if (player) {
      player.stop();
    }

    const finalSelection = Array.from(selected.values()).sort((a, b) => a - b);
    const probableSelected = finalSelection.filter((idx) => isProbable(inspect, idx)).length;

    trackValidated({
      fileId,
      reason,
      finalSelection,
      probableCount,
      probableSelectedCount: probableSelected,
      otherSelectedCount: finalSelection.length - probableSelected,
      auditionGlobalUsed: auditionGlobalUsedRef.current,
      auditionSoloCount: auditionSoloCountRef.current,
      msSinceShown: performance.now() - shownAtRef.current,
    });

    onContinue(finalSelection);
  }, [selected, inspect, fileId, reason, probableCount, player, onContinue]);

  const handleBypass = useCallback(() => {
    if (player) {
      player.stop();
    }

    trackBypassed({
      fileId,
      reason,
      msSinceShown: performance.now() - shownAtRef.current,
    });
    onBypass();
  }, [fileId, reason, player, onBypass]);

  const handleChooseAnother = useCallback(() => {
    if (player) {
      player.stop();
    }

    onChooseAnother();
  }, [player, onChooseAnother]);

  // Always-on cleanup for the pending debounce timer.
  useEffect(() => {
    return () => {
      if (selectionChangedTimerRef.current) {
        clearTimeout(selectionChangedTimerRef.current);
      }
    };
  }, []);

  if (!audition) {
    // Defensive guard: needs_help without audition is a bug. Render nothing
    // rather than crash, the modal still has the close affordance.
    return null;
  }

  const ctaLabel = resolveCtaLabel(selected.size);
  const ctaDisabled = selected.size === 0;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4 px-6 py-5">
      <header className="flex flex-col gap-2">
        <h1
          className="leading-snug"
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            background: HEADING_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {wording.title}
          {wording.subtitle && (
            <>
              <br />
              <span style={{ fontWeight: 600 }}>{wording.subtitle}</span>
            </>
          )}
        </h1>
        <p
          className="text-[14px]"
          style={{
            color: "var(--color-text-secondary)",
            lineHeight: "22px",
            maxWidth: 540,
          }}
        >
          {wording.body}
        </p>
      </header>

      <AuditionPlayerButton
        player={player}
        state={state}
        durationSec={audition.durationSec}
        densityBuckets={globalDensity}
        selectedTracks={selectedTracks}
        onPlayed={handleGlobalPlayed}
      />

      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          backgroundColor: "rgba(255,255,255,0.02)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-card)",
        }}
      >
        <TrackSelector
          audition={audition}
          inspectTracks={inspect.tracks}
          player={player}
          state={state}
          selected={selected}
          onToggle={handleToggle}
          flags={flags}
          onSoloPlay={handleSoloPlayed}
          onOtherTracksExpanded={handleOtherTracksExpanded}
        />
      </div>

      <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
        <button
          type="button"
          onClick={handleValidate}
          disabled={ctaDisabled}
          className="inline-flex items-center transition-all duration-200"
          style={{
            height: 40,
            padding: "0 24px",
            borderRadius: 9999,
            backgroundColor: ctaDisabled
              ? "var(--color-accent-subtle)"
              : "var(--color-accent)",
            color: ctaDisabled
              ? "rgba(167,139,250,0.40)"
              : "rgba(255,255,255,0.98)",
            boxShadow: ctaDisabled
              ? "none"
              : "0 0 20px rgba(139,92,246,0.32), inset 0 1px 0 rgba(255,255,255,0.18)",
            fontSize: 13,
            fontWeight: 600,
            cursor: ctaDisabled ? "not-allowed" : "pointer",
          }}
        >
          {ctaLabel}
        </button>

        <button
          type="button"
          onClick={handleChooseAnother}
          className="inline-flex items-center transition-colors duration-150 cursor-pointer hover:text-[color:var(--color-text-primary)]"
          style={{
            height: 40,
            padding: "0 20px",
            borderRadius: 9999,
            backgroundColor: "transparent",
            color: "var(--color-text-secondary)",
            fontSize: 13,
            fontWeight: 500,
            border: "1px solid var(--color-border-subtle)",
          }}
        >
          Essayer un autre fichier
        </button>

        <button
          type="button"
          onClick={handleBypass}
          className="ml-auto text-[13px] underline-offset-2 hover:underline transition-colors duration-150 cursor-pointer hover:text-[color:var(--color-text-secondary)]"
          style={{ color: "var(--color-text-muted)" }}
        >
          Ouvrir quand même
        </button>
      </div>
    </div>
  );
}

function isProbable(inspect: InspectResult, trackIdx: number): boolean {
  const t = inspect.audition?.tracks[trackIdx];

  if (!t) {
    return false;
  }

  const inspectTrack = inspect.tracks.find((it) => it.index === t.index);

  return isProbableTrack(t, inspectTrack);
}

function resolveCtaLabel(count: number): string {
  if (count === 0) {
    return "Sélectionne au moins une piste";
  }

  if (count === 1) {
    return "Valider 1 piste";
  }

  return `Valider ${count} pistes`;
}
